/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * WorkOS Authentication Hook
 * 
 * IMPORTANT: This hook can ONLY be used when WorkOS is configured (VITE_WORKOS_CLIENT_ID is set).
 * Before using this hook, check if WorkOS is available using useWorkOSAvailability().
 * 
 * For components that need to conditionally render WorkOS login, use the WorkOSSignInButton
 * component from @shared/components/auth instead, which handles the availability check internally.
 * 
 * Usage (when you're sure WorkOS is available):
 * ```tsx
 * // Only use this in components that are conditionally rendered based on WorkOS availability
 * const { signInWithWorkOS, isLoading, error } = useWorkOSAuth();
 * ```
 */

import { useState, useCallback } from 'react';
import { useAuth as useWorkOSAuthKit } from '@workos-inc/authkit-react';
import { useAuth } from '@shared/contexts/AuthContext';
import type { LoginResponseData } from '@shared/types/responses/auth';

export interface UseWorkOSAuthResult {
  signInWithWorkOS: () => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for handling WorkOS authentication flow.
 * 
 * WARNING: This hook must only be called when inside an AuthKitProvider context
 * (i.e., when VITE_WORKOS_CLIENT_ID is configured). Use useWorkOSAvailability()
 * to check availability before rendering components that use this hook.
 */
export function useWorkOSAuth(): UseWorkOSAuthResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const workosAuth = useWorkOSAuthKit();
  const { refreshUser } = useAuth();

  const signInWithWorkOS = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Initiate WorkOS sign-in flow
      // This will open a popup or redirect to WorkOS hosted UI
      await workosAuth.signIn();

      // After successful sign-in, get the access token
      const accessToken = await workosAuth.getAccessToken();

      if (!accessToken) {
        throw new Error('Failed to get WorkOS access token');
      }

      // Exchange WorkOS token for SkillShop tokens
      const response = await fetch('/api/auth/workos/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`WorkOS verification failed: ${errorText}`);
      }

      const responseJson = await response.json();
      if (!responseJson.success) {
        throw new Error(responseJson.error?.message || 'WorkOS verification failed');
      }

      const result = responseJson.data as LoginResponseData;

      // Store SkillShop tokens
      localStorage.setItem('accessToken', result.accessToken);
      localStorage.setItem('refreshToken', result.refreshToken);

      // Refresh user state in AuthContext
      await refreshUser();

    } catch (err) {
      console.error('WorkOS login failed:', err);
      const errorObj = err instanceof Error ? err : new Error('WorkOS login failed');
      setError(errorObj);
      throw errorObj;
    } finally {
      setIsLoading(false);
    }
  }, [workosAuth, refreshUser]);

  return {
    signInWithWorkOS,
    isLoading,
    error
  };
}

export default useWorkOSAuth;
